import { useCampaignsStore } from '~/stores/campaigns'
import { useCampaignDetailStore } from '~/stores/campaignDetail'

// Global callbacks registry for dashboard refresh
const dashboardRefreshCallbacks = new Set<() => void>()

/**
 * Centralized WebSocket listener for campaign events.
 */
export function useCampaignSync() {
    const campaignsStore = useCampaignsStore()
    const detailStore = useCampaignDetailStore()

    const setupListeners = () => {
        useEcho('campaign')
            .listen('.CampaignUpdated', (event: CampaignEvent) => {
                campaignsStore.handleCampaignUpdated(event.id)

                if (detailStore.campaign?.id === event.id) {
                    detailStore.refreshCampaign(true)
                }
            })
            .listen('.CampaignCreated', () => {
                campaignsStore.handleCampaignRefresh()
            })
            .listen('.CampaignRefresh', () => {
                campaignsStore.handleCampaignRefresh()

                if (detailStore.campaign) {
                    detailStore.refreshCampaign(true)
                }

                // Trigger dashboard refresh callbacks
                dashboardRefreshCallbacks.forEach(cb => cb())
            })
    }

    return {
        setupListeners,
    }
}

/**
 * Register a callback to be called when CampaignRefresh event is received.
 */
export function onCampaignRefresh(callback: () => void) {
    dashboardRefreshCallbacks.add(callback)

    onBeforeUnmount(() => {
        dashboardRefreshCallbacks.delete(callback)
    })
}

/**
 * Composable for components that need to react to campaign events locally.
 */
export function useCampaignEvents(options: {
    onUpdated?: (event: CampaignEvent) => void
    onCreated?: () => void
    onRefresh?: () => void
} = {}) {
    const echo = useEcho('campaign')

    if (options.onUpdated) {
        echo.listen('.CampaignUpdated', options.onUpdated)
    }
    if (options.onCreated) {
        echo.listen('.CampaignCreated', options.onCreated)
    }
    if (options.onRefresh) {
        echo.listen('.CampaignRefresh', options.onRefresh)
    }

    return echo
}
